import { z } from 'zod';
import { pluralize } from '../pluralize';
import { secretNamesIn } from './agent-draft/workflow-steps';
import { optionalAgentIdSchema } from './agent-tool-schemas';
// What's left before an agent can take real calls and chats, worked out from the app's data so the checklist can't
// claim something is done that isn't. The card re-checks each step as the user works through it.
const nextStepSchema = z.object({
    id: z.string(),
    kind: z.enum(['save', 'check', 'secret', 'tests', 'publish', 'go-live', 'test']),
    title: z.string(),
    detail: z.string().optional(),
    done: z.boolean(),
    /** The user's role can't do this step; says who can. */
    blockedBy: z.string().optional(),
    secretName: z.string().optional()
});
export const showNextStepsToolSchema = {
    name: 'show_next_steps',
    description: "Show the user a checklist card of what's left before an agent can take real calls and chats: saving, secrets its actions read that don't exist yet, problems check_agent finds, whether it has test cases, publishing and what that changes live, putting it on a phone number, and a test call. Each step has a button that takes the user there and back. Call it after building or changing an agent, instead of listing these steps yourself.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema }),
    outputSchema: z.object({ agentId: z.string(), agentName: z.string(), steps: z.array(nextStepSchema) })
};
/** What publishing would change live, in a sentence or two. */
export const describeLiveImpact = ({ switchesOnPublish, staysOnLabel }) => {
    if (switchesOnPublish.length === 0 && staysOnLabel.length === 0)
        return 'Nothing live uses this agent yet.';
    return [
        switchesOnPublish.length > 0 &&
            `Switches right away: ${switchesOnPublish.map(({ name }) => name).join(', ')}.`,
        staysOnLabel.length > 0 &&
            `Stays on its label: ${staysOnLabel.map(({ name, follows }) => `${name} (${follows})`).join(', ')}.`
    ]
        .filter(Boolean)
        .join(' ');
};
// The steps that change as the user works, so the card can rebuild them from live data.
export const saveStep = (unsavedChanges) => ({
    id: 'save',
    kind: 'save',
    title: unsavedChanges ? 'Save your changes' : 'Changes saved',
    done: !unsavedChanges
});
export const testCasesStep = (testCases) => ({
    id: 'tests',
    kind: 'tests',
    title: testCases > 0 ? `Has ${pluralize(testCases, 'test case')}` : 'Write test cases',
    ...(testCases === 0 && { detail: 'Nothing shows when a change breaks this agent yet.' }),
    done: testCases > 0
});
export const publishStep = (deployment) => ({
    id: 'publish',
    kind: 'publish',
    title: 'Publish',
    detail: describeLiveImpact(deployment),
    done: !deployment.unpublishedChanges && deployment.versions.length > 0
});
export const goLiveStep = ({ switchesOnPublish, staysOnLabel }) => {
    const isLive = switchesOnPublish.length > 0 || staysOnLabel.length > 0;
    return {
        id: 'go-live',
        kind: 'go-live',
        title: isLive ? 'In use' : 'Put it on a phone number',
        ...(!isLive && { detail: 'Or run it in a campaign, or add its chat widget to your site.' }),
        done: isLive
    };
};
export const planNextSteps = ({ draft, unsavedChanges, problems, existingSecrets, testCases, deployment, canManageSecrets, canPublish }) => {
    const secretSteps = existingSecrets
        ? secretNamesIn([draft.actions, draft.postCallWorkflow]).map((secretName) => ({
            id: `secret-${secretName}`,
            kind: 'secret',
            title: `Create the secret ${secretName}`,
            detail: 'An action reads it. Enter its value here; Atlas never sees it.',
            done: existingSecrets.includes(secretName),
            secretName,
            ...(!canManageSecrets && {
                blockedBy: 'Only Admins and IT Admins can create secrets. Ask one to add it.'
            })
        }))
        : [];
    return [
        saveStep(unsavedChanges),
        {
            id: 'check',
            kind: 'check',
            title: problems > 0 ? `Fix ${pluralize(problems, 'problem')} in the agent` : 'Agent check passes',
            done: problems === 0
        },
        ...secretSteps,
        testCasesStep(testCases),
        {
            id: 'test',
            kind: 'test',
            title: 'Try it with a test call',
            detail: 'Optional. Worth doing before you publish.',
            done: false
        },
        { ...publishStep(deployment), ...(!canPublish && { blockedBy: "Your role can't publish agents." }) },
        goLiveStep(deployment)
    ];
};
//# sourceMappingURL=next-steps.js.map